/**
 * eReg App Component
 *
 * Electronic regulatory document management with e-signatures.
 * Integrated into SiteOperations.io - uses shared authentication.
 * Note: EregProvider is now in App.tsx to persist state across navigation.
 */

import { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useEreg } from './contexts';
import { Layout } from './components/layout';
import { Dashboard, Protocols, Folders, Upload, Settings, IngestQueue } from './pages';
import { SignatureQueue } from './pages/SignatureQueue';
import { DelegationLogPage } from './pages/DelegationLog';
import { StudyCreate } from './pages/StudyCreate';
import { StudyDeepLink } from './pages/StudyDeepLink';
import { useAuth } from '../contexts/AuthContext';
import { ToastProvider } from '../ui';
import api from '../services/api';

// Protected route wrapper - uses shared auth from main app
function ProtectedRoute({ children, requireAdmin = false }: {
  children: React.ReactNode;
  requireAdmin?: boolean;
}) {
  const { session, user: authUser, currentSite } = useAuth();
  const { loading, isAdmin } = useEreg();
  const navigate = useNavigate();
  const [entitlementChecked, setEntitlementChecked] = useState(false);
  const [hasEntitlement, setHasEntitlement] = useState(false);
  const [checkingEntitlement, setCheckingEntitlement] = useState(true);

  // Check entitlement when component mounts or site changes
  useEffect(() => {
    const checkEntitlement = async () => {
      // Super admins always have access
      if (authUser?.role === 'super_admin') {
        setHasEntitlement(true);
        setEntitlementChecked(true);
        setCheckingEntitlement(false);
        return;
      }

      if (!currentSite) {
        // No site selected = no access to tools
        setHasEntitlement(false);
        setEntitlementChecked(true);
        setCheckingEntitlement(false);
        return;
      }

      try {
        const response = await api.get('/api/my-tools');
        const tools = response.data || [];
        const eregTool = tools.find((t: any) => t.tool_id === 'ereg');
        setHasEntitlement(eregTool?.is_available === true);
        setEntitlementChecked(true);
      } catch (error) {
        console.error('Error checking eReg entitlement:', error);
        setHasEntitlement(false);
        setEntitlementChecked(true);
      } finally {
        setCheckingEntitlement(false);
      }
    };

    if (session && !loading) {
      checkEntitlement();
    }
  }, [session, authUser?.role, currentSite, loading]);

  if (loading || checkingEntitlement) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 dark:border-primary-400"></div>
      </div>
    );
  }

  // If no session, redirect to main login
  if (!session) {
    return <Navigate to="/login" replace />;
  }

  // Check entitlement (super admins bypass this check)
  if (entitlementChecked && !hasEntitlement && authUser?.role !== 'super_admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center p-8 max-w-md">
          <div className="text-6xl mb-4">🔒</div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Access Denied</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            You do not have access to eReg for your current site.
            Please contact your administrator if you believe this is an error.
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150"
          >
            Return to Tool Hub
          </button>
        </div>
      </div>
    );
  }

  // If admin required but user is not admin
  if (requireAdmin && !isAdmin) {
    return <Navigate to="/ereg/dashboard" replace />;
  }

  return <>{children}</>;
}

// eReg Routes
function EregRoutes() {
  return (
    <Routes>
      {/* Default route goes to dashboard */}
      <Route path="/" element={<Navigate to="/ereg/dashboard" replace />} />

      {/* Dashboard */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Layout>
              <Dashboard />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Folder navigation */}
      <Route
        path="/folders"
        element={
          <ProtectedRoute>
            <Layout>
              <Folders />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Documents list - redirect to folders for now */}
      <Route
        path="/documents"
        element={<Navigate to="/ereg/folders" replace />}
      />

      {/* Upload */}
      <Route
        path="/upload"
        element={
          <ProtectedRoute>
            <Layout>
              <Upload />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Signature Queue */}
      <Route
        path="/signatures"
        element={
          <ProtectedRoute>
            <Layout>
              <SignatureQueue />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Delegation Log */}
      <Route
        path="/delegation"
        element={
          <ProtectedRoute>
            <Layout>
              <DelegationLogPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Email Repository / Ingest Queue */}
      <Route
        path="/repository"
        element={
          <ProtectedRoute>
            <Layout>
              <IngestQueue />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Admin: Study management */}
      <Route
        path="/protocols"
        element={
          <ProtectedRoute requireAdmin>
            <Layout>
              <Protocols />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Deep links (plan §3.6): create / study home / upload with return_to */}
      <Route
        path="/studies/new"
        element={
          <ProtectedRoute>
            <Layout>
              <StudyCreate />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/studies/:protocolId"
        element={
          <ProtectedRoute>
            <Layout>
              <StudyDeepLink mode="home" />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/studies/:protocolId/upload"
        element={
          <ProtectedRoute>
            <Layout>
              <StudyDeepLink mode="upload" />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Settings */}
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Layout>
              <Settings />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Catch all - redirect to dashboard */}
      <Route path="*" element={<Navigate to="/ereg/dashboard" replace />} />
    </Routes>
  );
}

// Main eReg App
// Note: Provider is now in App.tsx to persist state across navigation
export function EregApp() {
  return (
    <ToastProvider>
      <EregRoutes />
    </ToastProvider>
  );
}

export default EregApp;
